import { IKeycloakProfile } from "./interfaces/IKeycloakProfile";
import { IKeycloakResourceAccess, IMappedMs, IMappedMsRoles, IRoleMs } from "./interfaces/IKeycloakRoles";
import { keycloakConfig } from "./keycloakConfig";

export type onLoadOptionsType = "check-sso" | "login-required";

class KeycloakTools {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private static instance: any;
    public hasKeycloak: boolean;

    public getInstance() {
        if (!KeycloakTools.instance) {
            KeycloakTools.instance = global.Keycloak.default(keycloakConfig);
        }

        return KeycloakTools.instance;
    }

    public getToken(): string {
        this.setInstance();

        if (this.hasKeycloak) {
            return KeycloakTools.instance.token;
        }

        return "";
    }

    public getUserId(): string {
        this.setInstance();

        if (this.hasKeycloak && KeycloakTools.instance.tokenParsed && KeycloakTools.instance.tokenParsed.sub) {
            return KeycloakTools.instance.tokenParsed.sub;
        }

        return "";
    }

    public hasRealmRole(role: string) {
        this.setInstance();

        return this.getInstance().hasRealmRole(role);
    }

    public getUserResourceAccess(): IKeycloakResourceAccess | null {
        this.setInstance();

        if (
            this.hasKeycloak &&
            KeycloakTools.instance.tokenParsed &&
            KeycloakTools.instance.tokenParsed.resource_access
        ) {
            return KeycloakTools.instance.tokenParsed.resource_access;
        }

        return null;
    }

    public getUserRoles(mappedMs: IMappedMs) {
        const roles: IMappedMsRoles = {};
        const resourceAccess = this.getUserResourceAccess();
        Object.entries(mappedMs).forEach(([msName, msRoleName]) => {
            const msRoles = resourceAccess?.[msRoleName]?.roles;

            if (msRoles) {
                roles[msName] = msRoles;
            }
        });

        return roles;
    }

    public getChannels() {
        this.setInstance();

        if (
            this.hasKeycloak &&
            KeycloakTools.instance.tokenParsed &&
            KeycloakTools.instance.tokenParsed.salesChannels
        ) {
            return KeycloakTools.instance.tokenParsed.salesChannels;
        }

        return [];
    }

    public hasUserRole = (keycloakGroupRoles: IMappedMsRoles, featureRoleMsList: IRoleMs[]) => {
        if (this.isAuthenticated()) {
            return featureRoleMsList.every((featureRoleMs) => {
                const userMsRoleList = keycloakGroupRoles[featureRoleMs.msName];
                if (userMsRoleList) {
                    return featureRoleMs.roles.every((userRole) => userMsRoleList.includes(userRole));
                }
                return false;
            });
        }
        return false;
    };

    public hasResourceRole(role: string, resource?: string): boolean {
        this.setInstance();

        return this.getInstance().hasResourceRole(role, resource);
    }

    public async getUserProfile(): Promise<IKeycloakProfile | undefined> {
        this.setInstance();

        if (this.isAuthenticated()) {
            try {
                return await this.getInstance().loadUserProfile();
            } catch (error) {
                throw new Error("Couldn't load user's information.");
            }
        }

        return undefined;
    }

    // eslint-disable-next-line @typescript-eslint/ban-types
    public init(onLoadOption: onLoadOptionsType, otherOptions?: {}) {
        this.setInstance();

        if (this.hasKeycloak) {
            KeycloakTools.instance.init({
                onLoad: onLoadOption,
                promiseType: "native",
                ...otherOptions,
            });
        }
    }

    public isAuthenticated() {
        this.setInstance();

        if (this.hasKeycloak) {
            return KeycloakTools.instance.token !== undefined;
        }

        return false;
    }

    public login(options: { redirectUri: string; locale?: string }) {
        this.setInstance();

        if (this.hasKeycloak) {
            KeycloakTools.instance.login(options);
        }
    }

    public logout(options: { redirectUri: string }) {
        this.setInstance();

        if (this.hasKeycloak) {
            KeycloakTools.instance.logout(options);
        }
    }

    public setInstance() {
        if (global.Keycloak) {
            this.hasKeycloak = true;

            if (!KeycloakTools.instance) {
                KeycloakTools.instance = global.Keycloak(keycloakConfig);
            }
        } else {
            this.hasKeycloak = false;
        }
    }

    public setOnAuthError(onAuthError: () => void) {
        this.setInstance();

        if (this.hasKeycloak) {
            KeycloakTools.instance.onAuthError = onAuthError;
        }
    }

    public setOnAuthLogout(onAuthLogout: () => void) {
        this.setInstance();

        if (this.hasKeycloak) {
            KeycloakTools.instance.onAuthLogout = onAuthLogout;
        }
    }

    public setOnAuthSuccess(onAuthSuccess: () => void) {
        this.setInstance();

        if (this.hasKeycloak) {
            KeycloakTools.instance.onAuthSuccess = onAuthSuccess;
        }
    }

    public setOnReady(onReady: () => void) {
        this.setInstance();

        if (this.hasKeycloak) {
            KeycloakTools.instance.onReady = onReady;
        }
    }

    public async updateToken() {
        if (this.isAuthenticated()) {
            await KeycloakTools.instance.updateToken(60);
        }
    }
}

export const keycloakUtils = new KeycloakTools();
